import { Form, Head, usePage } from "@inertiajs/react"
import { Copy } from "lucide-react"
import { useState } from "react"

import HeadingSmall from "@/components/heading-small"
import { Button } from "@/components/ui/button"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import AppLayout from "@/layouts/app-layout"
import { referralCodesPath, signUpPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

interface ReferralCode {
  id: number
  code: string
  used_at: string | null
  used_by_name: string | null
  created_at: string
}

interface Props {
  referral_codes: ReferralCode[]
  [key: string]: unknown
}

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Referrals",
    href: referralCodesPath(),
  },
]

export default function ReferralCodesIndex() {
  const { referral_codes = [] } = usePage<Props>().props
  const [copiedId, setCopiedId] = useState<number | null>(null)

  function copyToClipboard(rc: ReferralCode) {
    const url = `${window.location.origin}${signUpPath()}?referral_code=${rc.code}`
    void navigator.clipboard.writeText(url).then(() => {
      setCopiedId(rc.id)
      setTimeout(() => setCopiedId(null), 2000)
    })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Referrals" />

      <div className="px-4 py-6">
        <div className="max-w-xl space-y-6">
          <HeadingSmall
            title="Referral codes"
            description="Generate single-use codes to invite others to sign up."
          />

          <Form method="post" action={referralCodesPath()}>
            {({ processing }) => (
              <Button type="submit" disabled={processing}>
                Generate referral code
              </Button>
            )}
          </Form>

          {referral_codes.length > 0 && (
            <ul className="divide-y rounded-md border">
              {referral_codes.map((rc) => (
                <li
                  key={rc.id}
                  className="flex items-center justify-between px-4 py-3"
                >
                  <div>
                    <span className="font-mono text-sm font-medium">
                      {rc.code}
                    </span>
                    {rc.used_at && (
                      <p className="text-muted-foreground text-xs">
                        Redeemed{" "}
                        {new Date(rc.used_at).toLocaleDateString(undefined, {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                        {rc.used_by_name ? ` by ${rc.used_by_name}` : ""}
                      </p>
                    )}
                  </div>

                  {rc.used_at ? (
                    <span className="text-muted-foreground text-xs">Used</span>
                  ) : (
                    <TooltipProvider>
                      <Tooltip open={copiedId === rc.id}>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(rc)}
                          >
                            <Copy className="h-4 w-4" />
                            Copy link
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Copied!</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </AppLayout>
  )
}
